import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Alert } from '@/types'

interface AlertState {
  alerts: Alert[]
  selectedAlert: Alert | null
  isLoading: boolean
  error: string | null
  lastUpdated: string | null
  unreadCount: number
  priorityCounts: {
    critical: number
    high: number
    warning: number
    info: number
  }
}

const initialState: AlertState = {
  alerts: [],
  selectedAlert: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
  unreadCount: 0,
  priorityCounts: {
    critical: 0,
    high: 0,
    warning: 0,
    info: 0,
  },
}

const alertSlice = createSlice({
  name: 'alerts',
  initialState,
  reducers: {
    setAlerts: (state, action: PayloadAction<Alert[]>) => {
      state.alerts = action.payload
      state.lastUpdated = new Date().toISOString()
      state.error = null
      
      // Update counts
      state.unreadCount = action.payload.filter(alert => alert.status === 'new').length
      state.priorityCounts = action.payload.reduce(
        (counts, alert) => {
          const priority = alert.priority as keyof typeof counts
          if (priority && Object.prototype.hasOwnProperty.call(counts, priority)) {
            counts[priority]++
          }
          return counts
        },
        { critical: 0, high: 0, warning: 0, info: 0 }
      )
    },
    addAlert: (state, action: PayloadAction<Alert>) => {
      state.alerts.unshift(action.payload)
      state.unreadCount++
      const priority = action.payload.priority as keyof typeof state.priorityCounts
      if (priority && Object.prototype.hasOwnProperty.call(state.priorityCounts, priority)) {
        state.priorityCounts[priority]++
      }
      state.lastUpdated = new Date().toISOString()
    },
    updateAlert: (state, action: PayloadAction<{ id: string; updates: Partial<Alert> }>) => {
      const index = state.alerts.findIndex(alert => alert.id === action.payload.id)
      if (index !== -1) {
        const oldAlert = state.alerts[index]
        const updatedAlert = { ...oldAlert, ...action.payload.updates }
        state.alerts[index] = updatedAlert
        
        // Update counts if status or priority changed
        if (oldAlert.status === 'new' && updatedAlert.status !== 'new') {
          state.unreadCount = Math.max(0, state.unreadCount - 1)
        }
        if (oldAlert.priority !== updatedAlert.priority) {
          const oldPriority = oldAlert.priority as keyof typeof state.priorityCounts
          const newPriority = updatedAlert.priority as keyof typeof state.priorityCounts
          if (oldPriority && Object.prototype.hasOwnProperty.call(state.priorityCounts, oldPriority)) {
            state.priorityCounts[oldPriority]--
          }
          if (newPriority && Object.prototype.hasOwnProperty.call(state.priorityCounts, newPriority)) {
            state.priorityCounts[newPriority]++
          }
        }
        
        // Update selected alert if it's the one being updated
        if (state.selectedAlert?.id === action.payload.id) {
          state.selectedAlert = updatedAlert
        }
        
        state.lastUpdated = new Date().toISOString()
      }
    },
    removeAlert: (state, action: PayloadAction<string>) => {
      const index = state.alerts.findIndex(alert => alert.id === action.payload)
      if (index !== -1) {
        const alert = state.alerts[index]
        state.alerts.splice(index, 1)
        
        if (alert.status === 'new') {
          state.unreadCount = Math.max(0, state.unreadCount - 1)
        }
        const priority = alert.priority as keyof typeof state.priorityCounts
        if (priority && Object.prototype.hasOwnProperty.call(state.priorityCounts, priority)) {
          state.priorityCounts[priority]--
        }
        
        if (state.selectedAlert?.id === action.payload) {
          state.selectedAlert = null
        }
        
        state.lastUpdated = new Date().toISOString()
      }
    },
    setSelectedAlert: (state, action: PayloadAction<Alert | null>) => {
      state.selectedAlert = action.payload
    },
    acknowledgeAlert: (state, action: PayloadAction<{ id: string; userId: string }>) => {
      const index = state.alerts.findIndex(alert => alert.id === action.payload.id)
      if (index !== -1) {
        const alert = state.alerts[index]
        state.alerts[index] = {
          ...alert,
          status: 'acknowledged',
          acknowledgedBy: action.payload.userId,
          acknowledgedAt: new Date().toISOString(),
        }
        
        if (alert.status === 'new') {
          state.unreadCount = Math.max(0, state.unreadCount - 1)
        }
        
        if (state.selectedAlert?.id === action.payload.id) {
          state.selectedAlert = state.alerts[index]
        }
        
        state.lastUpdated = new Date().toISOString()
      }
    },
    acknowledgeMultipleAlerts: (state, action: PayloadAction<{ ids: string[]; userId: string }>) => {
      const timestamp = new Date().toISOString()
      let acknowledgedCount = 0
      
      action.payload.ids.forEach(id => {
        const index = state.alerts.findIndex(alert => alert.id === id)
        if (index !== -1 && state.alerts[index].status === 'new') {
          state.alerts[index] = {
            ...state.alerts[index],
            status: 'acknowledged',
            acknowledgedBy: action.payload.userId,
            acknowledgedAt: timestamp,
          }
          acknowledgedCount++
        }
      })
      
      state.unreadCount = Math.max(0, state.unreadCount - acknowledgedCount)
      state.lastUpdated = timestamp
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
      if (action.payload) {
        state.error = null
      }
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.isLoading = false
    },
    clearError: (state) => {
      state.error = null
    },
    markAllAsRead: (state) => {
      state.alerts.forEach(alert => {
        if (alert.status === 'new') {
          alert.status = 'acknowledged'
          alert.acknowledgedAt = new Date().toISOString()
        }
      })
      state.unreadCount = 0
      state.lastUpdated = new Date().toISOString()
    },
  },
})

export const {
  setAlerts,
  addAlert,
  updateAlert,
  removeAlert,
  setSelectedAlert,
  acknowledgeAlert,
  acknowledgeMultipleAlerts,
  setLoading,
  setError,
  clearError,
  markAllAsRead,
} = alertSlice.actions

// Selectors
export const selectAlerts = (state: { alerts: AlertState }) => state.alerts.alerts
export const selectSelectedAlert = (state: { alerts: AlertState }) => state.alerts.selectedAlert
export const selectAlertsLoading = (state: { alerts: AlertState }) => state.alerts.isLoading
export const selectAlertsError = (state: { alerts: AlertState }) => state.alerts.error
export const selectUnreadAlertsCount = (state: { alerts: AlertState }) => state.alerts.unreadCount
export const selectAlertPriorityCounts = (state: { alerts: AlertState }) => state.alerts.priorityCounts
export const selectAlertsLastUpdated = (state: { alerts: AlertState }) => state.alerts.lastUpdated

// Complex selectors
export const selectAlertsByPriority = (priority: Alert['priority']) => (state: { alerts: AlertState }) =>
  state.alerts.alerts.filter(alert => alert.priority === priority)

export const selectCriticalAlerts = (state: { alerts: AlertState }) =>
  state.alerts.alerts.filter(alert => alert.priority === 'critical' && alert.status === 'new')

export const selectNewAlerts = (state: { alerts: AlertState }) =>
  state.alerts.alerts.filter(alert => alert.status === 'new')

export const selectAlertsByEquipment = (equipmentId: string) => (state: { alerts: AlertState }) =>
  state.alerts.alerts.filter(alert => alert.equipmentId === equipmentId)

export default alertSlice.reducer